import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import '../../Styles/User/User.css'; // External CSS for styling
import axiosInstance from '../../interceptor/axiosInstance';
import AddUsers from './AddUsers';
import DownloadUser from './DownloadUser';
import EditUserPopup from './UserEdit.jsx';
import AddSingleUser from './AddSingleUser.jsx';
import UpdateUserProgram from './UpdateUserProgram.js';
import { toast } from '../Library/SubMenus/MCQAssessment/toastUtils';

const UserRow = React.memo(({ user, isLoadingPassword, onEdit, onGeneratePassword }) => {
  const role = Array.isArray(user.user_type)
    ? user.user_type[0].toLowerCase()
    : String(user.user_type).replace(/['(),]/g, '').toLowerCase();

  return (
    <tr className={user.is_active === false ? 'highlight-red' : ''}>
      <td>{user.first_name}</td>
      <td>{user.last_name}</td>
      <td>{user.email}</td>
      <td>{role === 'student' ? user.username : ''}</td>
      <td>{role === 'faculty' ? user.username : ''}</td>
      <td>{role}</td>
      <td className='edit-password'>
        <button className="edit-btn" onClick={() => onEdit(user)}>Edit</button>
        <button
          className={`edit-btn ${user.is_active === false ? 'disabled' : ''}`}
          onClick={() => onGeneratePassword(user)}
          disabled={user.is_active === false || isLoadingPassword}
        >
          {isLoadingPassword ? 'Generating...' : 'Generate Password'}
        </button>
      </td>
    </tr>
  );
});

// Skeleton Loader Component
const SkeletonLoader = ({ pageSize }) => {
  return (
    <div className="skeleton-loader">
      <div className="skeleton-header">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="skeleton-header-cell" style={{ width: i === 2 ? '20%' : i === 6 ? '15%' : '12%' }} />
        ))}
      </div>
      {[...Array(pageSize)].map((_, rowIndex) => (
        <div key={rowIndex} className="skeleton-row">
          <div className="skeleton-cell" style={{ width: '12%' }} />
          <div className="skeleton-cell" style={{ width: '12%' }} />
          <div className="skeleton-cell" style={{ width: '20%' }} />
          <div className="skeleton-cell" style={{ width: '12%' }} />
          <div className="skeleton-cell" style={{ width: '12%' }} />
          <div className="skeleton-cell" style={{ width: '12%' }} />
          <div className="skeleton-cell" style={{ width: '15%' }} />
        </div>
      ))}
    </div>
  );
};

// Empty State Component
const EmptyState = ({ searchTerm, roleFilter, onClearFilters }) => {
  const hasFilters = searchTerm || roleFilter;
  
  return (
    <div className="empty-state">
      <div className="empty-state-icon">
        {hasFilters ? '🔍' : '👥'}
      </div>
      <h3>{hasFilters ? 'No Users Found' : 'No Users Yet'}</h3>
      <p>
        {hasFilters
          ? `No users match your search criteria. Try adjusting your filters or search term.`
          : 'There are no users in the system yet. Click "Add User" to get started.'}
      </p>
      {hasFilters && (
        <button className="clear-filters-btn" onClick={onClearFilters}>
          Clear Filters
        </button>
      )}
    </div>
  );
};

const User = () => {
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [isAddUserPopupOpen, setIsAddUserPopupOpen] = useState(false);
  const [roles] = useState([]);
  const [loading, setLoading] = useState(true); // Changed to true for initial load
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [addUserType, setAddUserType] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownType, setDropdownType] = useState("");
  const [passwordLoad, setPasswordLoad] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [jumpToPage, setJumpToPage] = useState('');
  const [searching, setSearching] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showPasswordDisplay, setShowPasswordDisplay] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);

  // Refs for debounce and abort controller
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  const fetchUsers = (page = 1, type = '', username = '', size = pageSize) => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    const isSearching = username !== '';
    if (isSearching) {
      setSearching(true);
    } else {
      setLoading(true);
    }
  
    axiosInstance
      .get(`accounts/users/?page=${page}&type=${type}&username=${username}&page_size=${size}`, {
        signal: abortControllerRef.current.signal
      })
      .then((response) => {
        const { pagination, results } = response.data.data;
  
        setUsers(results);
        setCount(pagination.count);
        setTotalPages(Math.ceil(pagination.count / size));
        setCurrentPage(page);
      })
      .catch((error) => {
        if (error.name !== 'CanceledError') {
          toast.error('Error Fetching Users', error.response?.data?.message || 'Failed to load users');
        }
      })
      .finally(() => {
        setLoading(false);
        setSearching(false);
      });
  };
  
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      fetchUsers(1, dropdownType, searchTerm, pageSize);
    }, 500);

    // Cleanup on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchTerm, dropdownType, pageSize]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchUsers(currentPage + 1, dropdownType, searchTerm, pageSize);
    }
  };
  
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchUsers(currentPage - 1, dropdownType, searchTerm, pageSize);
    }
  };

  const handleFirstPage = () => {
    if (currentPage !== 1) {
      fetchUsers(1, dropdownType, searchTerm, pageSize);
    }
  };

  const handleLastPage = () => {
    if (currentPage !== totalPages) {
      fetchUsers(totalPages, dropdownType, searchTerm, pageSize);
    }
  };

  const handlePageJump = () => {
    const pageNumber = parseInt(jumpToPage, 10);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      fetchUsers(pageNumber, dropdownType, searchTerm, pageSize);
      setJumpToPage('');
    } else {
      toast.warning('Invalid Page', `Please enter a page number between 1 and ${totalPages}`);
    }
  };

  const getDisplayRange = () => {
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, count);
    return { start, end };
  };
  
  // Handle Edit button click
  const handleEditClick = useCallback((user) => {
    setEditUser(user);
    setIsEditPopupOpen(true);
  }, []);

  // Handle Save button in edit popup
  const handleSaveEdit = () => {
    axiosInstance
      .put(`accounts/org-users/${editUser.id}/`, editUser)
      .then((response) => {
        setUsers((prevUsers) =>
          prevUsers.map((u) => (u.id === editUser.id ? response.data : u))
        );
        setIsEditPopupOpen(false);
        toast.success('User Updated', 'User information updated successfully');
      })
      .catch((error) => {
        toast.error('Update Failed', error.response?.data?.message || 'Failed to update user');
      });
  };

  const handleGeneratePasswordClick = useCallback((user) => {
    setSelectedUserForPassword(user);
    setShowPasswordConfirm(true);
  }, []);

  const confirmGeneratePassword = () => {
    if (!selectedUserForPassword) return;
    
    setPasswordLoad(selectedUserForPassword.id);
    setShowPasswordConfirm(false);
    
    // API call to generate password
    axiosInstance
      .post('setpassword', { user_id: selectedUserForPassword.id })
      .then((response) => {
        const newPassword = response.data.data?.password;
        setGeneratedPassword(newPassword);
        setShowPasswordDisplay(true);
        setPasswordLoad(null);
        toast.success('Password Generated Successfully');
      })
      .catch((error) => {
        toast.error('Generation Failed', error.response?.data?.message || 'Failed to generate password');
        setPasswordLoad(null);
      });
  };

  const cancelGeneratePassword = () => {
    setShowPasswordConfirm(false);
    setSelectedUserForPassword(null);
  };

  const closePasswordDisplay = () => {
    setShowPasswordDisplay(false);
    setGeneratedPassword('');
    setSelectedUserForPassword(null);
  };

  const copyPasswordToClipboard = () => {
    navigator.clipboard.writeText(generatedPassword).then(() => {
      toast.success('Password Copied', 'Password has been copied to clipboard');
    }).catch((error) => {
      toast.error('Copy Failed', 'Failed to copy password to clipboard');
    });
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleAddUserType = (type) => {
    setAddUserType(type);
    setIsDropdownOpen(false);
    setIsAddUserPopupOpen(true);
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDropdownType('');
  };

  const handleDropdownChange = (e) => {
    const selectedType = e.target.value;
    setDropdownType(selectedType);
  };

  const handleRefresh = () => {
    fetchUsers(currentPage, dropdownType, searchTerm, pageSize);
    toast.info('Refreshed', 'User list has been refreshed');
  };

  return (
    <div className="user-container">

      <div className="top-user-btn">
      <UpdateUserProgram />
        <div className="add-user-container">
          <button className="add-user-btn" onClick={toggleDropdown}>
            Add User <span className="dropdown-icon">▼</span>
          </button>

          {/* Dropdown Options */}
          {isDropdownOpen && (
            <div className="dropdown-options">
              <div className="dropdown-option" onClick={() => handleAddUserType('single')}>
                Add Single User
              </div>
              <div className="dropdown-option" onClick={() => handleAddUserType('bulk')}>
                Add Bulk User
              </div>
            </div>
          )}
        </div>
        <DownloadUser />
        <button className="refresh-btn" onClick={handleRefresh}>Refresh</button>
      </div>

      <div className="drop-search-assement">
        <div className="search-wrapper" style={{ position: 'relative', flex: 1, maxWidth: '600px' }}>
          <svg
            style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#94a3b8' }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search by Name, Email, Registration Number or Employee Id"
            style={{ paddingLeft: '44px' }}
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: '#64748b',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Clear search"
            >
              ×
            </button>
          )}
          {searching && (
            <div
              style={{
                position: 'absolute',
                right: searchTerm ? '40px' : '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '18px',
                height: '18px',
                border: '2px solid #e2e8f0',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }}
            />
          )}
        </div>

        <div className="page-size-selector">
          <label htmlFor="pageSize">Show:</label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => {
              const newPageSize = Number(e.target.value);
              setPageSize(newPageSize);
            }}
            className="page-size-dropdown"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
          </select>
        </div>
      </div>

      {/* Result Count */}
      {!loading && users.length > 0 && (
        <div className="results-info" style={{
          padding: '12px 20px',
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          borderRadius: '6px',
          marginBottom: '16px',
          fontWeight: '600',
          color: '#1e40af',
          fontSize: '14px'
        }}>
          Showing {getDisplayRange().start}-{getDisplayRange().end} of {count} users
        </div>
      )}

      {loading ? (
        <SkeletonLoader pageSize={pageSize} />
      ) : users.length === 0 ? (
        <EmptyState
          searchTerm={searchTerm}
          roleFilter={dropdownType}
          onClearFilters={handleClearSearch}
        />
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Registration Number</th>
                <th>Employee Id</th>
                <th className="user-role-header">
                  <div className="role-filter-container">
                    <svg className="role-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    <select value={dropdownType} onChange={handleDropdownChange} className="role-dropdown">
                      <option value="">All Roles</option>
                      <option value="student">🎓 Students</option>
                      <option value="prospective_faculty">🎯 Prospective Faculty</option>
                      <option value="new_admission">🆕 New Admission</option>
                      <option value="faculty">👨‍🏫 Faculty</option>
                    </select>
                  </div>
                </th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  isLoadingPassword={passwordLoad === user.id}
                  onEdit={handleEditClick}
                  onGeneratePassword={handleGeneratePasswordClick}
                />
              ))}
            </tbody>
          </table>

          {/* Enhanced Pagination Controls */}
          <div className="pagination-controls">
            <button
              onClick={handleFirstPage}
              disabled={currentPage === 1}
              className="pagination-btn"
              title="First Page"
            >
              « First
            </button>
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              ‹ Previous
            </button>
            
            <div className="page-jump-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                Page {currentPage} of {totalPages}
              </span>
              {totalPages > 1 && (
                <>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={jumpToPage}
                    onChange={(e) => setJumpToPage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handlePageJump()}
                    placeholder="Go to..."
                    style={{
                      width: '80px',
                      padding: '6px 10px',
                      border: '2px solid #dbeafe',
                      borderRadius: '6px',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}
                  />
                  <button
                    onClick={handlePageJump}
                    disabled={!jumpToPage}
                    style={{
                      padding: '6px 12px',
                      background: jumpToPage ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : '#e2e8f0',
                      color: jumpToPage ? '#ffffff' : '#94a3b8',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: jumpToPage ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Go
                  </button>
                </>
              )}
            </div>

            <button
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              className="pagination-btn"
            >
              Next ›
            </button>
            <button
              onClick={handleLastPage}
              disabled={currentPage >= totalPages}
              className="pagination-btn"
              title="Last Page"
            >
              Last »
            </button>
          </div>
        </>
      )}

      {isEditPopupOpen && (
        <EditUserPopup
          user={editUser}
          roles={roles}
          onClose={() => setIsEditPopupOpen(false)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Popup for adding user */}
      {isAddUserPopupOpen && addUserType === 'single' && ReactDOM.createPortal(
        <div className="user-popup-overlay">
          <div className="user-popup-wrapper">
            <span className="user-popup-close" onClick={() => setIsAddUserPopupOpen(false)}>
              &times;
            </span>
            <AddSingleUser
              onClose={() => setIsAddUserPopupOpen(false)}
              onUserAdded={fetchUsers}
            />
          </div>
        </div>,
        document.body
      )}

      {isAddUserPopupOpen && addUserType === 'bulk' && ReactDOM.createPortal(
        <div className="user-popup-overlay">
          <div className="user-popup-wrapper">
            <span className="user-popup-close" onClick={() => setIsAddUserPopupOpen(false)}>
              &times;
            </span>
            <AddUsers
              onClose={() => setIsAddUserPopupOpen(false)}
              onUserAdded={fetchUsers}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Password Generation Confirmation Popup */}
      {showPasswordConfirm && ReactDOM.createPortal(
        <div className="password-confirm-overlay">
          <div className="password-confirm-modal">
            <div className="password-confirm-header">
              <h3>⚠️ Confirm Password Generation</h3>
              <button 
                className="password-confirm-close" 
                onClick={cancelGeneratePassword}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="password-confirm-content">
              <p className="password-confirm-message">
                Are you sure you want to generate a new password for <strong>{selectedUserForPassword?.username}</strong>?
              </p>
              <p className="password-confirm-warning">
                This will replace the current password and cannot be undone.
              </p>
            </div>
            <div className="password-confirm-actions">
              <button 
                className="password-confirm-cancel" 
                onClick={cancelGeneratePassword}
              >
                Cancel
              </button>
              <button 
                className="password-confirm-submit" 
                onClick={confirmGeneratePassword}
              >
                Yes, Generate Password
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Password Display Popup */}
      {showPasswordDisplay && ReactDOM.createPortal(
        <div className="password-display-overlay">
          <div className="password-display-modal">
            <div className="password-display-header">
              <h3>✅ Password Generated Successfully</h3>
              <button 
                className="password-display-close" 
                onClick={closePasswordDisplay}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="password-display-content">
              <p className="password-display-label">
                New password for <strong>{selectedUserForPassword?.username}</strong>:
              </p>
              <div className="password-display-box">
                <input 
                  type="text" 
                  value={generatedPassword} 
                  readOnly 
                  className="password-display-input"
                />
                <button 
                  className="password-copy-btn" 
                  onClick={copyPasswordToClipboard}
                  title="Copy to clipboard"
                >
                  📋 Copy
                </button>
              </div>
              <p className="password-display-note">
                ⚠️  Make sure to save this password. You won't be able to see it again.
              </p>
            </div>
            <div className="password-display-actions">
              <button 
                className="password-display-done" 
                onClick={closePasswordDisplay}
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default User;
